import { CMS_RHP_SLUG, useCMSParams, useQueryCMS } from '@/features/cms/services/CMS/queries'
import { NoDataState } from '@/features/cms/components/CMS/components/Layouts/NoDataState'
import { Row } from '@shipt/design-system-layouts'
import { LoadingIndicator } from '@shipt/design-system-loading'
import { LayoutRenderer } from '@/features/cms/components/CMS/components/Layouts'
import { useEffect } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { GHP_TO_RHP_DURATION } from '@/lib/datadog/vitals'
import { useQueryRetailerFees } from '@/features/shop/services/Fees/queries'
import { SEOHead } from '@/features/shared/components/SEOHeader/SEOHead'
import { useParams } from 'next/navigation'
import {
  useStoreId,
  useUserStoreImage,
  useUserStoreName,
} from '@/features/account/services/User/hooks'
import { type CMSPageData } from '@/features/cms/components/CMS/types'
import { type MainEntity, SEO_PAGE_TYPE } from '@/features/shared/components/SEOHeader/types'
import { getCanonicalUrl } from '@/features/shared/utils/seo/seoConfig'
import { getRetailerShopBreadcrumbs, getStoreCategory } from '@/features/shop/utils/seo'
import { layoutEagerComponents } from '@/features/cms/components/CMS/components/Layouts/LayoutEagerComponents'
import { type ParamsOf } from '@next-types/routes'
import { objectStorageUrl } from '@/features/shared/utils/objectStorageUrl'
import { NycOrdinanceFeeBanner } from '@/features/shop/components/RetailerHomepage/NycOrdinanceFeeBanner'
import { useQueryKits } from '@/features/shop/services/Kits/queries'

export const RetailerHomepage = () => {
  const cmsParams = useCMSParams(CMS_RHP_SLUG)
  const { data, isPending, error } = useQueryCMS({
    params: cmsParams,
    enabled: Boolean(cmsParams.storeParams.store_location_id),
  })
  const storeId = useStoreId()
  const params = useParams<ParamsOf<'/shop/[store_slug]'>>()
  const storeName = useUserStoreName()
  const { image: storeImage } = useUserStoreImage()
  const storeCategory = getStoreCategory(params?.store_slug)
  const canonicalUrl = getCanonicalUrl(`/shop/${params?.store_slug}`)
  const pageTitle = `${storeName} ${storeCategory} | Same-Day Delivery with Shipt`
  const pageDescription = `Enjoy ${storeName} same-day ${storeCategory.toLowerCase()} in as little as one hour with Shipt. Shop online now & have your favorite ${storeName} products brought right to your door.`

  const breadcrumbs = getRetailerShopBreadcrumbs(params?.store_slug, storeName)

  const entities: MainEntity[] = []

  entities.push({
    '@type': 'ItemList',
    itemListElement: [
      {
        '@type': SEO_PAGE_TYPE.LOCAL_BUSINESS,
        name: storeName,
        url: canonicalUrl,
        logo: storeImage,
      },
    ],
  })

  // prefetch retailer fees and kits data here so they are available for any CMS components
  // that need it. since these components are rendered near the top of the page,
  // fetching the data within the components themselves can cause noticeable layout
  // shifts. some of these components may not render at all, but it is still preferable
  // to prefetch the data upfront to avoid UI shifts when they do render.
  useQueryRetailerFees({ notifyOnChangeProps: [] })
  useQueryKits({ storeIdOverride: storeId, notifyOnChangeProps: [] })

  const categories =
    data?.content.content_type_id === 'retailer_home_page_layout' &&
    data.content.data?.body_section_b?.find((item) => item.content_type_id === 'standard_nav')?.data
      .nav_links

  if (categories) {
    entities.push({
      '@type': 'ItemList',
      itemListElement: categories.map(({ data: category }) => ({
        '@type': SEO_PAGE_TYPE.COLLECTION_PAGE,
        name: category.heading,
        url:
          category.link.type === 'relative'
            ? `https://www.shipt.com${category.link.url}`
            : category.link.url,
        image: category.image.src,
      })),
    })
  }

  const pageTypeSchema = {
    '@context': 'https://schema.org',
    '@type': [SEO_PAGE_TYPE.WEB_PAGE, SEO_PAGE_TYPE.COLLECTION_PAGE],
    name: pageTitle,
    description: pageDescription,
    url: canonicalUrl,
    image: {
      url: objectStorageUrl('get-started-hero.png'),
      width: 872,
      height: 1072,
    },
    mainEntity: entities,
  }

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        breadcrumbs={breadcrumbs}
        pageTypeSchema={pageTypeSchema}
      />
      <NycOrdinanceFeeBanner />
      <RetailerHomepageContent data={data} isPending={isPending} error={error} />
    </>
  )
}

const RetailerHomepageContent = ({
  data,
  isPending,
  error,
}: {
  data: CMSPageData | undefined
  isPending: boolean
  error: Error | null
}) => {
  useEffect(() => {
    // stop measuring transition, as the presence of data indicates that the cms content has loaded
    // or do we want to put the stop transition in an actual react component that fires when rendered? This seems hard...
    // this duration measures the time between using clicking a store on GHP
    // to when the CMS response comes back on RHP
    //
    if (data?.id) {
      datadogRum.stopDurationVital(GHP_TO_RHP_DURATION)
    }
  }, [data?.id])

  if (isPending)
    return (
      <Row align="center" justify="center" paddingTop="5xl">
        <LoadingIndicator size="xl" />
      </Row>
    )
  if (!data?.content.data || error) return <NoDataState />

  return <LayoutRenderer layoutData={data.content} layoutEagerComponents={layoutEagerComponents} />
}
